import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

/**
 * FeatureToggleService (Angular 18).
 *
 * Replaces the AngularJS legacy service at:
 *   apps/b2b-ui/common/services/featureToggle/featureToggle.service.js
 *
 * Exposed back to AngularJS via `downgradeInjectable` under the legacy name
 * `'FeatureToggleService'`, so existing consumers in b2b-ui keep working
 * without modification (see ng-downgraded-components.ts).
 */
@Injectable({ providedIn: 'root' })
export class FeatureToggleService {
  private http = inject(HttpClient);

  private features: string[] = [];
  private isLoaded = false;
  private loadPromise: Promise<string[]>;
  private resolveLoad!: (features: string[]) => void;
  private rejectLoad!: (reason?: unknown) => void;

  // leave this comment - it's for local development
  private readonly url = '/assets/data/features.json';
  private readonly tenantCart = 'TENANT_CART';

  constructor() {
    this.loadPromise = this.createDeferred();
  }

  load(): Promise<string[]> {
    firstValueFrom(this.http.get<string[]>(this.url))
      .then((data) => {
        this.features = data;
        this.isLoaded = true;
        this.resolveLoad(this.features);
      })
      .catch(() => {
        this.rejectLoad();
      });

    return this.loadPromise;
  }

  reload(): void {
    this.loadPromise = this.createDeferred();
    this.reset();
    this.load();
  }

  reset(): void {
    this.features = [];
    this.isLoaded = false;
  }

  has(id: string): boolean {
    return this.tenantCart === id || this.features.indexOf(id) > -1;
  }

  check(features?: string | string[] | null): boolean {
    if (!features) {
      return true;
    }
    return ([] as string[]).concat(features).some((feat) => this.has(feat));
  }

  loaded(): Promise<string[]> {
    return this.loadPromise;
  }

  fetch(): string[] {
    return this.features;
  }

  private createDeferred(): Promise<string[]> {
    return new Promise<string[]>((resolve, reject) => {
      this.resolveLoad = resolve;
      this.rejectLoad = reject;
    });
  }
}
